/**
 * DynamicBond - Motion-enabled bond component
 *
 * Subscribes to atom MotionValues and updates bond geometry in real-time.
 * Uses Framer Motion's useMotionTemplate for 60fps updates without React re-renders.
 *
 * Wrapped with React.memo (PIPH-69). The `recipe` prop comes from the
 * memoized useSVGGeneration output, so its identity is stable across
 * unrelated re-renders. The motion updates happen through framer's
 * MotionValues and bypass React entirely, so memoizing the React wrapper
 * doesn't interfere with 60fps position updates.
 */

import { memo, useEffect } from 'react';
import { motion, useTransform } from 'motion/react';
import { useMotion } from '../../contexts/MotionContext';
import { useFormatOverride } from '../../rendering/contexts/FormatOverrideContext';
import { computeBondEndPoints, computeLineAngleBondEndPoints } from '@alchemiesolns/bond-calc-pure';
import type { BondRecipe } from '../../rendering/useSVGGeneration';
import { useSelection } from '../../selection';
import { useFeedbackWithContext } from '../../feedback';

export interface DynamicBondProps {
  recipe: BondRecipe;
  highlightEnd?: 'atom1' | 'atom2' | null; // Which end of the bond to highlight (25% or 75%)
}

/**
 * Renders a single bond line/tube with motion-reactive endpoints
 */
export const DynamicBond = memo(function DynamicBond({ recipe }: DynamicBondProps) {
  const { getAtomMotion } = useMotion();
  const format = useFormatOverride();

  // Use bondId for feedback tracking
  const selection = useSelection({ optional: true });
  const feedback = useFeedbackWithContext(recipe.bondId);

  const atom1Motion = getAtomMotion(recipe.atom1Id);
  const atom2Motion = getAtomMotion(recipe.atom2Id);

  if (!atom1Motion || !atom2Motion) {
    console.warn(`[DynamicBond ${recipe.bondId}] Missing motion values for atoms`);
    return null;
  }

  // Check selection state
  const isSelected = selection?.isSelected(recipe.bondId) ?? false;
  const isHovered = selection?.isHovered(recipe.bondId) ?? false;

  // Sync selection state with FeedbackManager
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback]);

  // Pre-compute all tube transforms OUTSIDE the map (Rules of Hooks)
  // Calculate all endpoint transforms for all tubes at once
  // Motion v12: use function form with .get() for automatic dependency tracking
  const allTubeEndpoints = useTransform(() => {
    const x1 = atom1Motion.x.get();
    const y1 = atom1Motion.y.get();
    const x2 = atom2Motion.x.get();
    const y2 = atom2Motion.y.get();

    return recipe.tubes.map((tube) => {
      const a1Pos = [x1, y1, 0];
      const a2Pos = [x2, y2, 0];

      // Apply perpendicular offset for double/triple bonds
      const offsetEndPoints = computeBondEndPoints(a1Pos, a2Pos, [0, 0, 1], tube.offset);

      // Apply endpoint shortening based on element symbols
      const finalEndPoints = computeLineAngleBondEndPoints(
        recipe.element1,
        recipe.element2,
        offsetEndPoints.a1,
        offsetEndPoints.a2,
        recipe.atom1NeighborCount,
        recipe.atom2NeighborCount,
        recipe.order,
        tube.lengthFactor,
        tube.lineIndex,
        tube.centered,
        format,
        recipe.atom1VisibilityFlag,
        recipe.atom2VisibilityFlag
      );

      return {
        x1: finalEndPoints.a1[0],
        y1: finalEndPoints.a1[1],
        x2: finalEndPoints.a2[0],
        y2: finalEndPoints.a2[1],
      };
    });
  });

  // Pre-create coordinate transforms for all possible tubes (max 3 for triple bonds)
  // Always call the same number of hooks to satisfy Rules of Hooks
  const tubeCoords = [
    {
      x1: useTransform(allTubeEndpoints, (eps) => eps[0]?.x1 ?? 0),
      y1: useTransform(allTubeEndpoints, (eps) => eps[0]?.y1 ?? 0),
      x2: useTransform(allTubeEndpoints, (eps) => eps[0]?.x2 ?? 0),
      y2: useTransform(allTubeEndpoints, (eps) => eps[0]?.y2 ?? 0),
    },
    {
      x1: useTransform(allTubeEndpoints, (eps) => eps[1]?.x1 ?? 0),
      y1: useTransform(allTubeEndpoints, (eps) => eps[1]?.y1 ?? 0),
      x2: useTransform(allTubeEndpoints, (eps) => eps[1]?.x2 ?? 0),
      y2: useTransform(allTubeEndpoints, (eps) => eps[1]?.y2 ?? 0),
    },
    {
      x1: useTransform(allTubeEndpoints, (eps) => eps[2]?.x1 ?? 0),
      y1: useTransform(allTubeEndpoints, (eps) => eps[2]?.y1 ?? 0),
      x2: useTransform(allTubeEndpoints, (eps) => eps[2]?.x2 ?? 0),
      y2: useTransform(allTubeEndpoints, (eps) => eps[2]?.y2 ?? 0),
    },
  ];

  // Render each tube in the bond
  return (
    <>
      {recipe.tubes.map((tube, tubeIndex) => {
        const coords = tubeCoords[tubeIndex];

        return (
          <motion.line
            key={`${recipe.bondId}-${tube.lineIndex}`}
            id={recipe.bondId+'-'+tube.lineIndex}
            className="svg-bond dynamic-bond"
            data-bond-id={recipe.bondId}
            data-bond-index={String(recipe.bondIndex)}
            data-atom1-id={recipe.atom1Id}
            data-atom2-id={recipe.atom2Id}
            data-atom1-index={String(recipe.atom1Index)}
            data-atom2-index={String(recipe.atom2Index)}
            data-order={String(recipe.order)}
            data-line-index={String(tube.lineIndex)}
            data-visibility={recipe.visibility}
            x1={coords.x1}
            y1={coords.y1}
            x2={coords.x2}
            y2={coords.y2}
            stroke={feedback.currentStyle.stroke ?? 'black'}
            strokeWidth={format.bond.thickness}
            style={{ pointerEvents: 'none' }}
          />
        );
      })}
    </>
  );
});
